import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Box,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { normalizeChecklistItemOptions } from '../../utils/checklistItemUtils';

interface ChecklistItem {
  id?: number;
  checklist?: number;
  parent_item?: number | null;
  title: string;
  description: string;
  item_type: string;
  order: number;
  is_required: boolean;
  validation_rules: any;
  options: string[];
  default_value: string;
  allowed_file_types: string;
  max_file_size_mb: number;
  is_visible: boolean;
  show_when_parent_completed: boolean;
}

interface ItemTypeOption {
  value: string;
  label: string;
}

interface ItemEditorProps {
  open: boolean;
  onClose: () => void;
  item: ChecklistItem | null;
  onSave: (item: ChecklistItem) => void;
  itemTypeOptions: ItemTypeOption[];
}

const ItemEditor: React.FC<ItemEditorProps> = ({
  open,
  onClose,
  item,
  onSave,
  itemTypeOptions
}) => {
  const [form, setForm] = useState<ChecklistItem>({
    title: '',
    description: '',
    item_type: 'checkbox',
    order: 1,
    is_required: true,
    validation_rules: {},
    options: [],
    default_value: '',
    allowed_file_types: '',
    max_file_size_mb: 10,
    is_visible: true,
    show_when_parent_completed: false
  });

  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    if (item) {
      setForm({
        ...item,
        options: normalizeChecklistItemOptions(item.options),
        validation_rules: item.validation_rules ?? {},
      });
    } else {
      setForm({
        title: '',
        description: '',
        item_type: 'checkbox',
        order: 1,
        is_required: true,
        validation_rules: {},
        options: [],
        default_value: '',
        allowed_file_types: '',
        max_file_size_mb: 10,
        is_visible: true,
        show_when_parent_completed: false
      });
    }
    setNewOption('');
  }, [item, open]);

  const handleFormChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddOption = () => {
    if (newOption.trim() && !form.options.includes(newOption.trim())) {
      setForm(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const handleRemoveOption = (optionToRemove: string) => {
    setForm(prev => ({
      ...prev,
      options: prev.options.filter(option => option !== optionToRemove)
    }));
  };

  const handleSave = () => {
    if (!form.title.trim()) {
      return; // Don't save if title is empty
    }
    onSave(form);
  };

  const handleCancel = () => {
    onClose();
  };

  const renderItemTypeSpecificFields = () => {
    switch (form.item_type) {
      case 'text_input':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Default Value"
              value={form.default_value}
              onChange={(e) => handleFormChange('default_value', e.target.value)}
              helperText="Optional default text for this field"
            />
          </Grid>
        );

      case 'number_input':
        return (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Default Value"
                value={form.default_value}
                onChange={(e) => handleFormChange('default_value', e.target.value)}
                helperText="Optional default number"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Value"
                value={form.validation_rules.min_value || ''}
                onChange={(e) => handleFormChange('validation_rules', {
                  ...form.validation_rules,
                  min_value: e.target.value ? parseInt(e.target.value) : undefined
                })}
                helperText="Minimum allowed value"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Value"
                value={form.validation_rules.max_value || ''}
                onChange={(e) => handleFormChange('validation_rules', {
                  ...form.validation_rules,
                  max_value: e.target.value ? parseInt(e.target.value) : undefined
                })}
                helperText="Maximum allowed value"
              />
            </Grid>
          </>
        );

      case 'file_upload':
        return (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Allowed File Types"
                value={form.allowed_file_types}
                onChange={(e) => handleFormChange('allowed_file_types', e.target.value)}
                helperText="e.g., .pdf,.doc,.jpg (comma-separated)"
                placeholder=".pdf,.doc,.jpg"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max File Size (MB)"
                value={form.max_file_size_mb}
                onChange={(e) => handleFormChange('max_file_size_mb', parseInt(e.target.value) || 10)}
                inputProps={{ min: 1, max: 100 }}
                helperText="Maximum file size in megabytes"
              />
            </Grid>
          </>
        );

      case 'select':
      case 'multi_select':
      case 'radio':
        return (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Options
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                label="Add Option"
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                placeholder="Enter option text"
              />
              <Button
                variant="outlined"
                onClick={handleAddOption}
                disabled={!newOption.trim()}
              >
                Add
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {form.options.map((option, index) => (
                <Chip
                  key={index}
                  label={option}
                  onDelete={() => handleRemoveOption(option)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            {form.options.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No options added yet. Add options for users to choose from.
              </Typography>
            )}
          </Grid>
        );

      case 'date_input':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="date"
              label="Default Date"
              value={form.default_value}
              onChange={(e) => handleFormChange('default_value', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Optional default date"
            />
          </Grid>
        );

      case 'time_input':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="time"
              label="Default Time"
              value={form.default_value}
              onChange={(e) => handleFormChange('default_value', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Optional default time"
            />
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {item?.id ? 'Edit Item' : 'Add New Item'}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Item Title"
              value={form.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              required
              helperText="The question or instruction for this item"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={form.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              multiline
              rows={2}
              helperText="Optional additional context or instructions"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Item Type</InputLabel>
              <Select
                value={form.item_type}
                label="Item Type"
                onChange={(e) => handleFormChange('item_type', e.target.value)}
              >
                {itemTypeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Order"
              value={form.order}
              onChange={(e) => handleFormChange('order', parseInt(e.target.value) || 1)}
              inputProps={{ min: 1 }}
              helperText="Display order in the checklist"
            />
          </Grid>

          {/* Item Type Specific Fields */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Configuration
            </Typography>
          </Grid>
          
          {renderItemTypeSpecificFields()}

          {/* Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Settings
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_required}
                  onChange={(e) => handleFormChange('is_required', e.target.checked)}
                />
              }
              label="Required Item"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_visible}
                  onChange={(e) => handleFormChange('is_visible', e.target.checked)}
                />
              }
              label="Visible"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.show_when_parent_completed}
                  onChange={(e) => handleFormChange('show_when_parent_completed', e.target.checked)}
                />
              }
              label="Show when parent completed"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!form.title.trim()}
        >
          Save Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemEditor; 